import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import VuiBox from "components/VuiBox";
import { Card, Grid } from "@mui/material";
import { useApi } from "hooks/useAPI";
import { CompareService, SitewideDataPoint, SitewideMetric, Region } from "services/CompareService";
import { getLocalTime } from "utils/getLocalTime";
import { SITEWIDE_METRIC_LABELS, getMetricOptions, getRegionOptions } from "./types/SelectTypes";
import { getSitewideChartOptions } from "./config/ChartConfig";
import { generateSitewideChartSeries } from "./utils/DataProcessingUtils";
import CompareChart from "./components/CompareChart";
import MetricSelector from "./components/MetricSelector";
import RegionSelector from "./components/RegionSelector";


const SitewideCompareChart = () => {
  const { get } = useApi();
  const selectedMerchant = useSelector((state: any) => state.merchants.selectedMerchant);
  const { startDate, endDate } = useSelector((state: any) => state.common);
  const [loading, setLoading] = useState(false);
  const [merchantData, setMerchantData] = useState<SitewideDataPoint[]>([]);
  const [benchmarkData, setBenchmarkData] = useState<SitewideDataPoint[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<SitewideMetric>("spend");
  const [selectedRegion, setSelectedRegion] = useState<Region | undefined>(undefined);

  const compareService = useMemo(() => new CompareService(get), []);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedMerchant?.id || !startDate || !endDate) return;

      setLoading(true);
      try {
        const { startDateString, endDateString } = getLocalTime(startDate, endDate);

        const result = await compareService.getCompareChart({
          merchantId: selectedMerchant.id,
          start: startDateString,
          end: endDateString,
          region: selectedRegion
        });

        setMerchantData(result.data.merchant);
        setBenchmarkData(result.data.benchmark);
      } catch (error) {
        console.error("Error fetching compare data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedMerchant?.id, startDate, endDate, compareService, selectedRegion]);

  const handleRegionChange = (selectedOption: any) => {
    setSelectedRegion(selectedOption && selectedOption.value ? selectedOption.value as Region : undefined);
  };
  
  const handleMetricChange = (option: any) => {
    setSelectedMetric(option.value);
  };

  const series = useMemo(() => generateSitewideChartSeries(merchantData, benchmarkData, selectedMetric, selectedRegion), [merchantData, benchmarkData, selectedMetric, selectedRegion]);

  const options = useMemo(() => getSitewideChartOptions(selectedMetric), [selectedMetric]);


  return (
    <Card>
      <VuiBox p={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MetricSelector
              title="Sitewide Compare"
              selectedMetric={selectedMetric}
              metricOptions={getMetricOptions(SITEWIDE_METRIC_LABELS)}
              onMetricChange={handleMetricChange}
            />
          </Grid>
          <Grid item xs={12}>
            <RegionSelector
              regionOptions={getRegionOptions()}
              onRegionChange={handleRegionChange}
            />
          </Grid>
        </Grid>
        <CompareChart
          title="Sitewide Compare"
          options={options}
          series={series}
          loading={loading}
          noDataMessage="No data available for the selected period"
        />
      </VuiBox>
    </Card>
  );
};

export default SitewideCompareChart; 